import type { QueryClient } from '@tanstack/react-query';
import { publishUIMessage } from '../shared/utils/notifications';
import { t } from '../utils/i18n';
import type { AppShellApiClient } from '../shared/types';

// All methods used by queries are now part of AppShellApiClient (src/shared/types/index.ts).
// This alias preserves the export for any existing imports.
export type QueriesApiClient = AppShellApiClient;

class ApiError extends Error {
  statusCode: number;
  constructor(message: string, statusCode: number) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
  }
}

export class NotFoundError extends Error {
  statusCode: number;
  constructor() {
    super('Resource not found');
    this.name = 'NotFoundError';
    this.statusCode = 404;
  }
}

export const keys = {
  trackingSessionMetadata: ['tracking_session_metadata'] as const,
  authToken: ['merchant_account_token'] as const,
  merchantAccountV2: (token: string) =>
    ['merchant_account', 'v2', token] as const,
  merchantV2: (merchantId: string, token: string) =>
    ['merchant', 'v2', merchantId, token] as const,
  merchantAppsV2: (merchantId: string, token: string) =>
    ['merchant_app', 'v2', merchantId, token] as const,
  locations: () => ['locations'] as const,
  merchantsSearch: (token: string) => ['merchantSearch', token] as const,
  merchantOnboardingsV2: {
    fetchOnboardingSteps: (
      merchantId: string | null | undefined,
      token: string
    ) => ['merchant_onboardings', 'v2', merchantId, token] as const,
    skipOnboardingStep: (
      merchantId: string | null | undefined,
      token: string
    ) =>
      ['merchant_onboardings', 'v2', 'skip_step', merchantId, token] as const,
  },
  merchantPaymentSettingsV2: (merchantId: string, token: string) =>
    ['merchant_payment_settings', merchantId, token] as const,
  merchantPaymentSettingsV2b: (merchantId: string, token: string) =>
    ['merchant_payment_settings_v2b', merchantId, token] as const,
  contractV2: {
    findAll: (organizationId: string, token: string) =>
      ['contractV2', 'findAll', organizationId, token] as const,
  },
  sms: {
    currentMonthUsage: (merchantId: string) =>
      ['sms', 'current_month_usage', merchantId] as const,
  },
  subscription: {
    latest: (merchantId: string | undefined) =>
      ['subscription', merchantId] as const,
    portalSession: (merchantId: string | undefined) =>
      ['subscriptionPortalSession', merchantId] as const,
  },
  paypal: {
    initiateSetup: (merchantId: string | undefined) =>
      ['paypal', 'initiateSetup', merchantId] as const,
  },
  bookingConfigsV2: (merchantId: string, token: string) =>
    ['bookingConfigs', 'v2', merchantId, token] as const,
};

function injectErrorMetadata(
  metadata: Record<string, unknown>
): (error: Error & { metadata?: unknown }) => Promise<never> {
  return (error) => {
    error.metadata = metadata;
    return Promise.reject(error);
  };
}

/**
 * Waits for the authToken query if it is currently fetching, then checks again.
 * Only throws "Missing auth token!" after the authToken query is no longer loading/fetching.
 */
function authenticatedQuery(queryClient: QueryClient) {
  async function waitForAuthTokenIfFetching(): Promise<string> {
    const queryKey = keys.authToken;

    // fast-path: already cached
    let token = queryClient.getQueryData<string>(queryKey);
    if (token) return token;

    // If the authToken query is in-flight, wait for it to resolve, then re-check cache
    const query = queryClient.getQueryCache().find({ queryKey });
    const isFetching = query?.state?.fetchStatus === 'fetching';

    // TanStack Query stores the in-flight promise on the Query instance (v4/v5).
    // This avoids starting a second request and simply awaits the existing one.
    if (isFetching && query?.promise) {
      await query.promise;
      token = queryClient.getQueryData<string>(queryKey);
      if (token) return token;
    }

    throw new Error('Missing auth token!');
  }

  return <TReturn, TArgs extends unknown[]>(
      configSetupFn: (
        withAuth: <T>(fn: (token: string) => Promise<T>) => Promise<T>,
        ...args: TArgs
      ) => TReturn
    ) =>
    (...args: TArgs): TReturn => {
      const withAuth = async <T>(
        fn: (token: string) => Promise<T>
      ): Promise<T> => {
        const token = await waitForAuthTokenIfFetching();
        return fn(token);
      };

      return configSetupFn(withAuth, ...args);
    };
}

function retryDelayFn(attemptIndex: number): number {
  return Math.min(1000 * 2 ** attemptIndex, 30000);
}

function retryUnless404(
  retryCount: number
): (failureCount: number, error: Error) => boolean {
  return (failureCount, error) => {
    if (error instanceof NotFoundError) {
      return false;
    }
    return failureCount < retryCount;
  };
}

// P3: type per-query return shapes when fetcher types are available in @shore/types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function handleApiResponse(response: Response): Promise<any> {
  if (response.status === 404) {
    throw new NotFoundError();
  }

  const errorResponse = response.status >= 400;
  const responseData = (await response.json()) as unknown;

  if (errorResponse) {
    throw new ApiError(JSON.stringify(responseData), response.status);
  }

  return responseData;
}

export default function createQueryConfig(
  queryClient: QueryClient,
  apiClient: AppShellApiClient
) {
  return {
    authToken: () => {
      const queryKey = keys.authToken;

      function fetchToken() {
        return apiClient.authV2
          .getOrCreateAccessToken()
          .then(String)
          .catch(injectErrorMetadata({ queryKey }));
      }

      return {
        queryKey,
        queryFn: fetchToken,
        gcTime: Infinity,
      };
    },

    subscription: authenticatedQuery(queryClient)(
      (
        withAuth,
        params: { merchantId?: string | null } | null | undefined,
        options: { enabled?: boolean } = {}
      ) => {
        const queryKey = keys.subscription.latest(
          params?.merchantId ?? undefined
        );
        const enabledFromCaller = options.enabled ?? true;

        async function fetchLatestSubscription() {
          return withAuth((token) =>
            apiClient.subscriptionV3
              .fetchLatestSubscription(params?.merchantId ?? '', token)
              .then(handleApiResponse)
              .catch(injectErrorMetadata({ queryKey, token }))
          );
        }

        return {
          queryKey,
          queryFn: fetchLatestSubscription,
          gcTime: Infinity,
          retry: retryUnless404(3),
          retryDelay: retryDelayFn,
          enabled: enabledFromCaller && Boolean(params?.merchantId),
        };
      }
    ),

    subscriptionPortalSession: authenticatedQuery(queryClient)(
      (withAuth, params: { merchantId?: string | null } | null | undefined) => {
        const queryKey = keys.subscription.portalSession(
          params?.merchantId ?? undefined
        );

        async function fetchPortalSession() {
          return withAuth((token) =>
            apiClient.subscriptionV3
              .fetchPortalSession(params?.merchantId ?? '', token)
              .then(handleApiResponse)
              .catch(injectErrorMetadata({ queryKey, token }))
          );
        }

        return {
          queryKey,
          queryFn: fetchPortalSession,
          gcTime: Infinity,
          retry: retryUnless404(3),
          retryDelay: retryDelayFn,
          enabled: Boolean(params?.merchantId),
        };
      }
    ),

    locations: () => {
      const queryKey = keys.locations();

      function fetchLocations() {
        return apiClient
          .getLocations()
          .catch(injectErrorMetadata({ queryKey }));
      }

      return {
        queryKey,
        queryFn: fetchLocations,
        gcTime: Infinity,
      };
    },

    downPayments: {
      setupDownPayments: authenticatedQuery(queryClient)(
        (withAuth, params: { merchantId?: string }) => {
          const mutationKey = ['down_payments', params?.merchantId];

          async function setupDownPayments({
            percentage,
            type,
          }: {
            percentage: number;
            type: string;
          }) {
            return withAuth((token) =>
              apiClient.downPayments
                .setupDownPayments(
                  params?.merchantId ?? '',
                  { percentage, type },
                  token
                )
                .then(handleApiResponse)
                .catch(
                  injectErrorMetadata({
                    mutationKey,
                    token,
                    merchantId: params?.merchantId,
                  })
                )
            );
          }

          return {
            mutationKey,
            mutationFn: setupDownPayments,
            gcTime: Infinity,
            onSuccess: () =>
              publishUIMessage(
                'success',
                t(
                  'notifications.down-payment-configurations.success-message'
                ) || ''
              ),
            onError: () =>
              publishUIMessage(
                'error',
                t('notifications.down-payment-configurations.error-message') ||
                  ''
              ),
          };
        }
      ),

      disableDownPayments: authenticatedQuery(queryClient)(
        (withAuth, params: { merchantId?: string }) => {
          const mutationKey = ['down_payments', params?.merchantId];

          async function disableDownPayments() {
            return withAuth((token) =>
              apiClient.downPayments
                .disableDownPayments(params?.merchantId ?? '', token)
                .then(handleApiResponse)
                .catch(
                  injectErrorMetadata({
                    mutationKey,
                    token,
                    merchantId: params?.merchantId,
                  })
                )
            );
          }

          return {
            mutationKey,
            mutationFn: disableDownPayments,
            gcTime: Infinity,
            onSuccess: () =>
              publishUIMessage(
                'success',
                t(
                  'notifications.down-payment-configurations.success-message'
                ) || ''
              ),
            onError: () =>
              publishUIMessage(
                'error',
                t('notifications.down-payment-configurations.error-message') ||
                  ''
              ),
          };
        }
      ),
    },

    smsCurrentMonthUsage: (merchantId: string) => {
      const queryKey = keys.sms.currentMonthUsage(merchantId);
      const now = new Date();

      function fetchSmsCurrentMonthUsage() {
        return apiClient.smsUsage.findAll({
          filter: {
            month: now.getMonth() + 1,
            year: now.getFullYear(),
            merchant_id: merchantId,
          },
        });
      }

      return {
        queryKey,
        queryFn: fetchSmsCurrentMonthUsage,
        gcTime: Infinity,
        retry: retryUnless404(3),
        retryDelay: retryDelayFn,
      };
    },

    merchantAccountV2: authenticatedQuery(queryClient)((withAuth) => {
      const tokenForKey =
        queryClient.getQueryData<string>(keys.authToken) || 'loading';
      const queryKey = keys.merchantAccountV2(String(tokenForKey));

      async function fetchMerchantAccount() {
        return withAuth((token) =>
          apiClient.merchantAccountV2
            .me({
              fields: {
                merchant_account: ['employee'],
                organizations: ['key_account_contract', 'test', 'name'],
              },
              include: ['employee', 'organization'],
            })
            .catch(injectErrorMetadata({ queryKey, token }))
        );
      }

      return {
        queryKey,
        queryFn: fetchMerchantAccount,
      };
    }),

    merchantV2: authenticatedQuery(queryClient)(
      (withAuth, merchantId: string) => {
        const tokenForKey =
          queryClient.getQueryData<string>(keys.authToken) || 'loading';
        const queryKey = keys.merchantV2(merchantId, String(tokenForKey));

        async function fetchMerchant() {
          const merchants = [
            'feature_toggles',
            'name',
            'organization',
            'created_at',
            'currency',
            'source',
            'ui_visibility',
            'category',
            'pixel_id',
            'slug',
            'phone',
            'time_zone',
          ];

          const organizations = [
            'id',
            'trial_ends_on',
            'trial_days_left',
            'self_sign_up',
            'test',
            'package_code',
            'whitelabel_slug',
          ];

          return withAuth((token) =>
            apiClient.merchantV2
              .find(merchantId, {
                include: ['organization'],
                fields: { organizations, merchants },
              })
              .catch(injectErrorMetadata({ merchantId, queryKey, token }))
          );
        }

        return {
          queryKey,
          queryFn: fetchMerchant,
        };
      }
    ),

    merchantAppsV2: authenticatedQuery(queryClient)(
      (withAuth, merchantId: string) => {
        const tokenForKey =
          queryClient.getQueryData<string>(keys.authToken) || 'loading';
        const queryKey = keys.merchantAppsV2(merchantId, String(tokenForKey));

        async function fetchSessionPermissions() {
          return withAuth((token) =>
            apiClient.merchantAppsV2
              .find(merchantId)
              .catch(injectErrorMetadata({ merchantId, queryKey, token }))
          );
        }

        return {
          queryKey,
          queryFn: fetchSessionPermissions,
        };
      }
    ),

    merchantsSearch: authenticatedQuery(queryClient)((withAuth) => {
      const tokenForKey =
        queryClient.getQueryData<string>(keys.authToken) || 'loading';
      const queryKey = keys.merchantsSearch(String(tokenForKey));

      async function searchMerchant() {
        return withAuth((token) =>
          apiClient.algolia
            .search({
              keyFilter: [apiClient.algolia.MERCHANTS],
              options: {
                attributesToRetrieve: ['name', 'objectID'],
              },
            })
            .catch(injectErrorMetadata({ queryKey, token }))
        );
      }

      return {
        queryKey,
        queryFn: searchMerchant,
      };
    }),

    merchantOnboardingsV2: {
      fetchOnboardingSteps: authenticatedQuery(queryClient)(
        (
          withAuth,
          params:
            | { merchantId?: string | null; locale?: string }
            | null
            | undefined
        ) => {
          const tokenForKey =
            queryClient.getQueryData<string>(keys.authToken) || 'loading';
          const queryKey = keys.merchantOnboardingsV2.fetchOnboardingSteps(
            params?.merchantId,
            String(tokenForKey)
          );

          async function fetchMerchantOnboardingSteps() {
            return withAuth((token) =>
              apiClient.merchantOnboardingsV2
                .fetchOnboardingSteps({
                  merchantId: params?.merchantId ?? '',
                  params: {
                    locale: params?.locale || 'en',
                  },
                })
                .catch(injectErrorMetadata({ queryKey, token }))
            );
          }

          return {
            queryKey,
            queryFn: fetchMerchantOnboardingSteps,
            enabled: Boolean(params?.merchantId),
          };
        }
      ),

      skipOnboardingStep: authenticatedQuery(queryClient)(
        (withAuth, params: { merchantId?: string | null }) => {
          const tokenForKey =
            queryClient.getQueryData<string>(keys.authToken) || 'loading';
          const mutationKey = keys.merchantOnboardingsV2.skipOnboardingStep(
            params?.merchantId,
            String(tokenForKey)
          );

          async function skipLastMerchantOnboardingStep() {
            return withAuth((token) =>
              apiClient.merchantOnboardingsV2
                .skipOnboardingStep({
                  merchantId: params?.merchantId ?? '',
                  params: {
                    // For simplicity, we are hardcoding the step to skip.
                    // In the future, we can allow passing the step to skip as a parameter if needed.
                    step_id: 'setup_shore_pay',
                  },
                })
                .then(handleApiResponse)
                .catch(
                  injectErrorMetadata({
                    mutationKey,
                    token,
                    merchantId: params?.merchantId,
                  })
                )
            );
          }

          return {
            mutationKey,
            mutationFn: skipLastMerchantOnboardingStep,
          };
        }
      ),
    },

    merchantPaymentSettingsV2: authenticatedQuery(queryClient)(
      (withAuth, params: { merchantId?: string | null }) => {
        const tokenForKey =
          queryClient.getQueryData<string>(keys.authToken) || 'loading';
        const queryKey = keys.merchantPaymentSettingsV2(
          params.merchantId ?? '',
          String(tokenForKey)
        );

        async function fetchMerchantPaymentSettings() {
          return withAuth((token) =>
            apiClient.merchantPaymentSettingsV2
              .getPaymentMethods()
              .catch(injectErrorMetadata({ queryKey, token }))
          );
        }

        return {
          queryKey,
          queryFn: fetchMerchantPaymentSettings,
        };
      }
    ),

    merchantPaymentSettingsV2b: authenticatedQuery(queryClient)(
      (withAuth, params: { merchantId?: string | null }) => {
        const tokenForKey =
          queryClient.getQueryData<string>(keys.authToken) || 'loading';
        const queryKey = keys.merchantPaymentSettingsV2b(
          params.merchantId ?? '',
          String(tokenForKey)
        );

        async function fetchPaymentSettingsV2b() {
          return withAuth((token) =>
            apiClient.merchantPaymentSettingsV2b
              .getPaymentMethods()
              .catch(injectErrorMetadata({ queryKey, token }))
          );
        }

        return {
          queryKey,
          queryFn: fetchPaymentSettingsV2b,
        };
      }
    ),

    contractV2: {
      findAll: authenticatedQuery(queryClient)(
        (withAuth, organizationId: string) => {
          const tokenForKey =
            queryClient.getQueryData<string>(keys.authToken) || 'loading';
          const queryKey = keys.contractV2.findAll(
            organizationId,
            String(tokenForKey)
          );

          async function fetchContract() {
            return withAuth((token) =>
              apiClient.contractV2
                .findAll({
                  filter: { organization: organizationId },
                  include: ['packages', 'fees'],
                })
                .catch(injectErrorMetadata({ queryKey, token, organizationId }))
            );
          }

          return {
            queryKey,
            queryFn: fetchContract,
          };
        }
      ),
    },

    paypal: {
      initiateSetup: authenticatedQuery(queryClient)(
        (withAuth, params: { merchantId?: string }) => {
          const queryKey = keys.paypal.initiateSetup(params?.merchantId);

          async function createPaypalSetup() {
            return withAuth((token) =>
              apiClient.payPal.signupLink
                .create({ merchant: { id: params?.merchantId ?? '' } })
                .catch(injectErrorMetadata({ queryKey, token }))
            );
          }

          return {
            queryKey,
            queryFn: createPaypalSetup,
          };
        }
      ),
    },

    bookingConfigsV2: {
      find: authenticatedQuery(queryClient)(
        (withAuth, { merchantId }: { merchantId: string }) => {
          const tokenForKey =
            queryClient.getQueryData<string>(keys.authToken) || 'loading';
          const queryKey = keys.bookingConfigsV2(
            merchantId,
            String(tokenForKey)
          );

          async function fetchBookingConfigs() {
            return withAuth((token) =>
              apiClient.bookingConfigsV2
                .find(merchantId)
                .catch(injectErrorMetadata({ merchantId, queryKey, token }))
            );
          }

          return {
            queryKey,
            queryFn: fetchBookingConfigs,
          };
        }
      ),

      update: authenticatedQuery(queryClient)(
        (
          withAuth,
          { merchantId, payload }: { merchantId: string; payload: unknown }
        ) => {
          const tokenForKey =
            queryClient.getQueryData<string>(keys.authToken) || 'loading';
          const queryKey = keys.bookingConfigsV2(
            merchantId,
            String(tokenForKey)
          );

          async function updateBookingConfigs() {
            return withAuth((token) =>
              apiClient.bookingConfigsV2
                .update(merchantId, payload)
                .catch(injectErrorMetadata({ queryKey, token }))
            );
          }

          return {
            queryKey,
            queryFn: updateBookingConfigs,
          };
        }
      ),
    },

    ssoLink: {
      generatePOSLink: authenticatedQuery(queryClient)(
        (withAuth, params: { merchantId?: string }) => {
          const mutationKey = ['pos_sso_link', params?.merchantId];

          async function generatePosSsoLink() {
            return withAuth((token) =>
              apiClient.ssoLink
                .generatePosSsoLink({
                  merchantId: params?.merchantId,
                })
                .then(handleApiResponse)
                .catch(
                  injectErrorMetadata({
                    mutationKey,
                    token,
                    merchantId: params?.merchantId,
                  })
                )
            );
          }

          return {
            mutationKey,
            mutationFn: generatePosSsoLink,
            gcTime: Infinity,
          };
        }
      ),
    },
  };
}
